import { isMimeTypeMatch, normalizeUrl } from "@primoui/utils"
import type { useTranslations } from "next-intl"
import { z } from "zod"

type TFunction = ReturnType<typeof useTranslations>

export const ALLOWED_MIMETYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/avif",
]

const createPathSchema = (t: TFunction) => {
  return z.object({
    path: z.string().regex(/^[a-z0-9/_-]+$/i, { error: t("invalidPath") }),
  })
}

export const createFileSchema = (t: TFunction) => {
  return z
    .instanceof(File)
    .refine(({ size }) => size > 0, { error: t("fileCannotBeEmpty") })
    .refine(({ size }) => size < 1024 * 512, { error: t("fileSizeTooLarge") })
    .refine(({ type }) => isMimeTypeMatch(type, ALLOWED_MIMETYPES), { error: t("fileTypeInvalid") })
}

export const createSubmitToolSchema = (t: TFunction) => {
  return z.object({
    name: z.string().min(1, { error: t("required") }),
    websiteUrl: z
      .url(t("invalidUrl"))
      .min(1, { error: t("required") })
      .trim()
      .toLowerCase()
      .transform(url => normalizeUrl(url)),
    submitterNote: z
      .string()
      .max(256, { error: issue => t("maxLength", { length: Number(issue.maximum) }) }),
    newsletterOptIn: z.boolean().optional().default(false),
  })
}

export const createNewsletterSchema = (t: TFunction) => {
  return z.object({
    captcha: z.literal("").optional(),
    email: z.email({ error: t("invalidEmail") }),
  })
}

export const createLegalUpdatesSchema = createNewsletterSchema

export const createReportToolSchema = (t: TFunction) => {
  return z.object({
    type: z.string().min(1, { error: t("required") }),
    email: z.email({ error: t("invalidEmail") }),
    message: z
      .string()
      .max(256, { error: issue => t("maxLength", { length: Number(issue.maximum) }) }),
    toolId: z.string(),
  })
}

export const createFeedbackSchema = (t: TFunction) => {
  return z.object({
    email: z.email({ error: t("invalidEmail") }),
    message: z.string().min(1, { error: t("required") }),
  })
}
export type FeedbackSchema = z.input<ReturnType<typeof createFeedbackSchema>>

export const createFetchMediaSchema = (t: TFunction) => {
  return createPathSchema(t).extend({
    url: z
      .url({ error: t("invalidUrl") })
      .min(1, { error: t("required") })
      .trim(),
    type: z.enum(["favicon", "screenshot"]).default("favicon"),
  })
}

export const createUploadMediaSchema = (t: TFunction) => {
  return createPathSchema(t).extend({
    file: createFileSchema(t),
  })
}
